import GameLayout from '~/pages/authentified/GameLayout';
import styled from 'styled-components';
import { Menu } from '~/components/ui/menu';
import logo from '@assets/images/logo/logo-trainpursuit.png';
import bg from '@assets/images/background/mode-solo-bg.png';
import bgQuiz from '@assets/images/background/bg-quiz.png';
import { Position, positions } from '~/utils/common/position';
import { useEffect, useRef, useState } from 'react';
import iconPin from '@assets/images/icon/icon-pin.png';
import quizTimeText from '@assets/images/background/quiz-time-text.png';
import iconGigt from '@assets/images/icon/icon-gift.png';
import iconMedal from '@assets/images/icon/icon-medal.png';
import { useMutation, useQuery } from '@apollo/client';
import {
  GQL_CREATE_TRACKING,
  StoredSoloTracking,
} from '~/gql/tracking/tracking';
import { authClient } from '~/helpers/apollo';
import { notification } from 'antd';
import { useLingui } from '@lingui/react';
import GameFooter from '~/pages/authentified/GameFooter';
import { ChoiceTheme } from '~/components/game/choice-theme';
import {
  GQL_RANDOM_QUESTIONS,
  StoredQuestion,
} from '~/gql/questions/questions';
import { GQL_THEMES, StoredTheme } from '~/gql/theme/theme';
import { ThemePresentation } from '~/components/game/theme-presentation';
import { RenderQuestions } from '~/components/game/render-questions';
import {
  SPFeedbackPosition,
  SSpanTitle,
  SSpanTitleExtraSmall,
} from '~/components/styles/styles';
import { classic } from '~/themes';
import { useAuth } from '~/helpers/store/auth';
import { useNavigate } from 'react-router-dom';
import RulesSoloDialog from '~/components/game/rules-solo-dialog';
import { IStateAnswer } from '~/data/types';
import { SPFeedback } from '~/components/game/choice-battle-theme';
import { ButtonQuit } from '~/components/ui/button-quit';
import QuitDialog from '~/components/game/quit-dialog';

/*
steps:
  0 => positions[0] hors plateau
  1 => après createTracking avec une reele position
  2 => quiz time
  3 => choix d'un thème
  4 => thème choisie
  5 => render questions
  6 => Result (case ordinaire, case 13)
  7 => Feedback, vers /home au click
*/

function ModeSoloPage() {
  const { i18n } = useLingui();
  const { payload } = useAuth();
  const navigate = useNavigate();
  const [soloTracking, setSoloTracking] = useState<
    StoredSoloTracking | undefined
  >(undefined);
  const [position, setPosition] = useState<Position>(positions[0]);
  const [newPositionId, setNewPositionId] = useState<number | undefined>(
    undefined
  );
  const [step, setStep] = useState<number | undefined>(undefined);
  const [themeId, setThemeId] = useState<string | undefined>(undefined);
  const [theme, setTheme] = useState<StoredTheme | undefined>(undefined);
  const [themes, setThemes] = useState<StoredTheme[]>([]);
  const [questions, setQuestions] = useState<StoredQuestion[]>([]);
  const [diff, setDiff] = useState<number>(0);
  const [textFeedbackPosition, setTextFeedbackPosition] = useState<string>('');
  const [turn, setTurn] = useState<number>(0);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isRulesNotSeen, setIsRulesNotSeen] = useState<boolean>(false);
  const [isQuitDialog, setIsQuitDialog] = useState<boolean>(false);
  const [percentResponses, setPercentResponses] = useState<number | null>(null);
  const [currentThemeAnswers, setCurrentThemeAnswers] = useState<
    IStateAnswer[]
  >([]);

  const newPositionRef = useRef<number | undefined>(newPositionId);

  const { refetch: fetchThemes } = useQuery(GQL_THEMES, {
    client: authClient,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data) {
        setThemes(data.getThemes);
      }
    },
  });

  const { refetch: fetchQuestions } = useQuery(GQL_RANDOM_QUESTIONS, {
    variables: {
      themeId: themeId,
      trackingId: soloTracking?.id || '',
    },
    client: authClient,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: themeId === undefined || soloTracking === undefined,
    onCompleted: (data) => {
      if (data) {
        setQuestions(data.getRandomQuestions.questions);
        setCurrentThemeAnswers(
          data.getRandomQuestions.currentThemeAnswers.map((a) => ({
            nb: a.index,
            isAnswerCorrect: a.isCorrect,
          }))
        );
      }
    },
  });

  useEffect(() => {
    if (step === 1 && !themeId) {
      setTimeout(() => {
        setStep(2);
        setTimeout(() => {
          !isCompleted && setStep(3);
        }, 800);
      }, 800);
    }
  }, [step]);

  const [createTracking] = useMutation(GQL_CREATE_TRACKING, {
    client: authClient,
    onCompleted: (data) => {
      setTimeout(() => {
        setSoloTracking(data.createTracking);
        setStep(1);
        if (
          data.createTracking.position > 0 &&
          data.createTracking.position <= 13
        ) {
          setPosition(positions[data.createTracking.position]);
        }
      }, 800);
    },
    onError: () => {
      notification.error({
        message: i18n._('create.tracking'),
        description: i18n._('bad.create.tracking'),
        placement: 'bottomLeft',
      });
    },
  });

  useEffect(() => {
    void getThemes();

    const isRules = localStorage.getItem('epsf-rs');
    if (isRules) {
      setIsRulesNotSeen(false);
      void initGame();
    } else {
      setIsRulesNotSeen(true);
    }
  }, [localStorage.getItem('epsf-rs')]);

  function handleRulesSeen() {
    localStorage.setItem('epsf-rs', '1');
    // void initGame
    setIsRulesNotSeen(false);
  }

  async function getThemes() {
    await fetchThemes();
  }

  async function initGame() {
    setStep(0);
    setPosition(positions[0]);
    setThemeId(undefined);
    setTheme(undefined);
    await createNewTracking();
  }

  async function createNewTracking() {
    await createTracking();
  }

  async function getQuestions() {
    await fetchQuestions();
  }

  useEffect(() => {
    if (themeId) {
      getQuestions();
    }
  }, [themeId]);

  const handleSelectTheme = (name: string) => {
    const _themes = [...themes];
    const selectedTheme = _themes.find((el) => el.name === name);
    if (selectedTheme) {
      setThemeId(selectedTheme.id);
      setTheme(selectedTheme);
      setTimeout(() => {
        setStep(4);
      }, 800);
    }
  };

  const step1 = (
    <>
      <SDivStep hidden={!(step === 0 || step === 1)}>
        <SImgLogo src={logo} alt="" />
        <SDiv></SDiv>
      </SDivStep>
    </>
  );

  const step2 = (
    <>
      <SDivStep hidden={step !== 2}>
        <SDivStepContent $background={bgQuiz}>
          <SQuizTitleImg src={quizTimeText} alt="" />
        </SDivStepContent>
      </SDivStep>
    </>
  );

  const step3 = (
    <>
      <SDivStep hidden={step !== 3}>
        <SDivImgLogo />
        <SDiv>
          {step === 3 && (
            <ChoiceTheme
              theme={theme}
              turn={turn}
              themes={themes}
              onSelectTheme={handleSelectTheme}
            />
          )}
        </SDiv>
      </SDivStep>
    </>
  );

  const step4 = (
    <>
      <SDivStep hidden={step !== 4}>
        <SDivImgLogo />
        <SDiv>
          {theme && step === 4 && (
            <ThemePresentation
              modeName="Mode solo"
              theme={theme}
              onClick={() => setStep(5)}
            />
          )}
        </SDiv>
      </SDivStep>
    </>
  );

  const step5 = (
    <>
      <SDivStep hidden={step !== 5}>
        <SDivActivities>
          {step === 5 && soloTracking && theme && (
            <RenderQuestions
              onTurnChange={setTurn}
              theme={theme}
              trackingId={soloTracking?.id}
              onFinish={() => handleQuestionsFinish()}
              onNewPosition={setNewPositionId}
              questions={questions}
              setIsCompleted={setIsCompleted}
              setResponsesPercent={setPercentResponses}
              initStateAnswer={currentThemeAnswers}
              lastQuestion={position.id === 13}
            />
          )}
        </SDivActivities>
      </SDivStep>
    </>
  );

  const step6 = (
    <>
      <SDivStep hidden={step !== 6} $justifyContent="flex-start">
        <SDivImgLogo />
        {position.id === 13 ? (
          <SDivFeedback $succes>
            <SSpanTitle $color={classic.token?.colorWhite}>
              {isCompleted ? 'Gagné' : 'Dernière question'} !
            </SSpanTitle>
          </SDivFeedback>
        ) : (
          <SDivFeedbackPosition diff={diff}>
            {diff === 0 ? (
              <SPFeedback $margin="15px 0">{textFeedbackPosition}</SPFeedback>
            ) : (
              <SPFeedbackPosition $margin="15px 0" diff={diff}>
                {textFeedbackPosition}
              </SPFeedbackPosition>
            )}
          </SDivFeedbackPosition>
        )}
      </SDivStep>
    </>
  );

  const step7 = (
    <>
      <SDivStep hidden={step !== 7}>
        <SDivStepContent
          $background={bgQuiz}
          $cursorPointer
          onClick={() => navigate('/home/classement')}
        >
          <SSpanTitle
            $color={classic.token?.colorWhite}
          >{`Bravo ${payload?.username} !`}</SSpanTitle>
          <SDivFin>
            <SGigtImg src={iconGigt} alt="" />
            <SDivGiftMedal>
              <SImgGiftMedal src={iconMedal} alt="" />
              {percentResponses && (
                <SDivScoreText>
                  <SSpanTitle
                    $marginBottom="0"
                    $color={classic.token?.colorWhite}
                  >
                    {percentResponses}
                    {' %'}
                  </SSpanTitle>
                  <SSpanTitleExtraSmall $color={classic.token?.colorWhite}>
                    {i18n._('solo.good.answers')}
                  </SSpanTitleExtraSmall>
                </SDivScoreText>
              )}
            </SDivGiftMedal>
          </SDivFin>
        </SDivStepContent>
      </SDivStep>
    </>
  );

  useEffect(() => {
    newPositionRef.current = newPositionId;
  }, [newPositionId]);

  useEffect(() => {
    let textPosition: string = '';
    const oldPosition = position.id;
    const _newPosition = newPositionId || position.id;

    const _diff = _newPosition - oldPosition;
    if (_diff > 0) {
      if (_diff === 1) textPosition = `+ ${_diff} case !`;
      else textPosition = `+ ${_diff} cases !`;
    }
    if (_diff < 0) {
      if (_diff === -1) textPosition = `- ${Math.abs(_diff)} case...`;
      else textPosition = `- ${Math.abs(_diff)} cases...`;
    }
    if (_diff === 0) {
      textPosition = "Vous n'avancez pas !";
    }

    setTextFeedbackPosition(textPosition);
    setDiff(_diff);
  }, [newPositionId]);

  function handleQuestionsFinish() {
    setStep(6);
    setTheme(undefined);

    const _newPosition = newPositionRef.current || position.id;

    setTimeout(() => {
      setPosition(positions[_newPosition]);
    }, 800);

    setTimeout(() => {
      if (isCompleted) {
        setStep(7);
      } else {
        setTheme(undefined);
        setThemeId(undefined);
        setStep(1);
      }
    }, 1600);
  }

  return (
    <GameLayout background={bg} withoutHeader withoutFooter>
      {(step === 0 || step === 1 || step === 6) && (
        <SPositionWrapper>
          <SPosition>
            <img src={bg} alt="" />
            <SPin $top={position.top} $left={position.left}>
              <img src={iconPin} alt="" />
            </SPin>
          </SPosition>
        </SPositionWrapper>
      )}

      <Container>
        <SDivMenu>
          <Menu withoutActions />
          <ButtonQuit
            onClick={() => {
              if (step === 7) {
                navigate('/home/classement');
              } else {
                setIsQuitDialog(true);
              }
            }}
          />
        </SDivMenu>
        <SDivFooter>
          <GameFooter setIsRulesNotSeen={setIsRulesNotSeen} />
        </SDivFooter>

        <SDivContent>
          {(step === 2 ||
            step === 3 ||
            step === 4 ||
            step === 5 ||
            step === 7) && <SDivBlur $background />}

          {step1}
          {step2}
          {step3}
          {step4}
          {step5}
          {step6}
          {step7}
        </SDivContent>
        {(step === 3 || step === 4) && <SImgLogo src={logo} alt="" />}
      </Container>
      <RulesSoloDialog onClose={handleRulesSeen} open={isRulesNotSeen} />
      <QuitDialog onClose={() => setIsQuitDialog(false)} open={isQuitDialog} />
    </GameLayout>
  );
}

export default ModeSoloPage;

const SDivScoreText = styled.div`
  align-items: center;
  flex-direction: column;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 30%;
  left: 50%;
  width: 52%;
  transform: translate(-50%, -20%);
`;

const SDivBlur = styled.div<{ $background?: boolean }>`
  background: ${({ $background }) =>
    $background
      ? `linear-gradient(180deg, rgba(0, 202, 191, 0.60) 0%, rgba(0, 173, 212, 0.60) 100%)`
      : 'none'};
  backdrop-filter: ${({ $background }) =>
    $background ? `blur(18px)` : 'none'};
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const SDivStep = styled.div<{
  hidden?: boolean;
  $justifyContent?: string;
  $background?: boolean;
}>`
  align-items: center;
  background: ${({ $background }) =>
    $background
      ? `linear-gradient(180deg, rgba(0, 202, 191, 0.60) 0%, rgba(0, 173, 212, 0.60) 100%)`
      : 'none'};
  backdrop-filter: ${({ $background }) =>
    $background ? `blur(18px)` : 'none'};
  background-position: center;
  background-size: auto 100%;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $justifyContent }) => $justifyContent || 'center'};
  left: 0;
  position: absolute;
  top: 0;
  z-index: 1;

  height: ${({ hidden }) => (hidden ? '0' : '100%')};
  transition: ${({ hidden }) => (hidden ? 'none' : 'opacity 0.5s linear')};
  opacity: ${({ hidden }) => (hidden ? '0' : '1')};
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
  width: ${({ hidden }) => (hidden ? '0' : '100%')};
`;

const SDivStepContent = styled.div<{
  justifyContent?: string;
  $background?: string;
  $cursorPointer?: boolean;
}>`
  align-items: center;
  display: flex;
  cursor: ${({ $cursorPointer }) => ($cursorPointer ? 'pointer' : 'auto')};
  flex-direction: column;
  background-image: ${({ $background }) =>
    $background ? `url('${$background}')` : 'none'};
  background-position: center;
  background-size: auto 100%;
  background-repeat: no-repeat;
  justify-content: ${({ justifyContent }) => justifyContent || 'center'};
  position: relative;
  height: 100%;
  width: 100%;
`;

const SQuizTitleImg = styled.img`
  height: 35%;
`;

const SDivFin = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  height: 70%;
  position: relative;
  width: 100%;
`;

const SGigtImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  object-fit: contain;
  width: 100%;
  height: 100%;
`;

const SDivGiftMedal = styled.div`
  bottom: 0;
  position: absolute;
  right: 0;
  width: 70%;
  height: 70%;
`;

const SImgGiftMedal = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  object-fit: contain;
  width: 100%;
  height: 100%;
`;

const SDivFeedbackPosition = styled.div<{ diff: number }>`
  background: ${({ diff }) => {
    if (diff > 0)
      return 'linear-gradient(90deg, rgba(45, 191, 112, 0.00) 0%, #2DBF70 50%, rgba(45, 191, 112, 0.00) 100%)';
    if (diff < 0)
      return 'linear-gradient(90deg, rgba(204, 62, 88, 0.00) 0%, #CC3E58 50%, rgba(204, 62, 88, 0.00) 100%)';
    return 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 50%, rgba(255, 255, 255, 0) 100%)';
  }};
  position: absolute;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  padding: 0 30px;
  width: auto;
`;

const SDivFeedback = styled.div<{ $succes?: boolean }>`
  align-items: flex-end;
  display: flex;
  justify-content: center;
  background: ${({ $succes }) =>
    $succes
      ? 'linear-gradient(180deg, rgba(45, 191, 112, 0.00) 0%, #2DBF70 100%)'
      : 'none'};
  position: absolute;
  padding-bottom: 20px;
  left: 0;
  bottom: 0;
  height: 30%;
  width: 100%;
`;

const SPositionWrapper = styled.div`
  position: absolute;
  align-items: center;
  display: flex;
  justify-content: center;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const SPosition = styled.div`
  height: 100%;
  position: relative;
  width: auto;

  img {
    height: 100%;
  }
`;

const SPin = styled.div<{ $top?: number; $left?: number }>`
  position: absolute;
  top: ${({ $top }) => ($top ? `${$top}%` : '50%')};
  left: ${({ $left }) => ($left ? `${$left}%` : '50%')};
  height: 12%;

  img {
    position: absolute;
    left: 0;
    top: 0;
    object-fit: contain;
    width: auto;
    height: 100%;
  }
`;

const SDivImgLogo = styled.div`
  height: 12%;
  width: auto;

  @media screen and (orientation: portrait) {
    height: 15%;
  }

  @media screen and (max-width: 450px) {
    height: 12%;
  }
`;

const SImgLogo = styled.img`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 12%;
  width: auto;

  @media screen and (orientation: portrait) {
    height: 15%;
  }

  @media screen and (max-width: 450px) {
    height: 12%;
  }
`;

const SDiv = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: 88%;
  width: 100%;
`;

const SDivActivities = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const SDivContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  height: 100%;
  width: 100%;
`;

const SDivMenu = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  left: 0;
  top: 0;
  position: absolute;
  padding: 5px;
  width: 100%;
  z-index: 2;

  @media screen and (orientation: portrait) {
    padding: 5px;
  }
  @media screen and (max-height: 450px) {
    padding: 5px;
  }
`;

const SDivFooter = styled.div`
  left: 0;
  bottom: 0;
  position: absolute;
  width: 100%;
  pointer-events: none;
  z-index: 2;
`;
